import type { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useEffectiveWeekStartDay } from "../../hooks/useEffectiveWeekStartDay";
import { getTodayStaffingDate, getWeekStart } from "../../utils/dateUtils";
import { type CalendarPager, type CalendarSelection, COLLAPSED_WEEK_COUNT, EXPANDED_WEEK_COUNT } from "./calendarPager";

/** The first and last day there is a schedule for. Undefined while it loads, which holds the pager on its page. */
export type CalendarPagerBounds = {
	firstDay: DateTime;
	lastDay: DateTime;
};

const isWithin = (date: DateTime, start: DateTime, end: DateTime): boolean => date >= start && date <= end;

/**
 * Pages a 2- or 4-week calendar and keeps one selected day for it and the list below it. Paging selects today when
 * the new page holds it, otherwise the page's first day; resizing re-anchors the page on the selected day's week.
 * The page is stored as an anchor day, not a week start, so changing the start-of-week day re-aligns it. Today is the
 * department's schedule day, so before its day start the previous day is still today.
 */
export const useCalendarPager = (bounds: CalendarPagerBounds | undefined): CalendarPager => {
	const weekStartDay = useEffectiveWeekStartDay();
	const { chosenDepartment } = useChosenDepartment();
	const today = getTodayStaffingDate(chosenDepartment.dayStart);
	const [pageAnchor, setPageAnchor] = useState<DateTime>(today);
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [selection, setSelection] = useState<CalendarSelection>({ date: today, scroll: "instant" });
	const [anchoredWeekStartDay, setAnchoredWeekStartDay] = useState<number>(weekStartDay);

	// A new start-of-week day re-cuts the weeks; re-anchor on the selected day so it stays on the page.
	if (anchoredWeekStartDay !== weekStartDay) {
		setAnchoredWeekStartDay(weekStartDay);
		setPageAnchor(selection.date);
	}

	const weekCount = isExpanded ? EXPANDED_WEEK_COUNT : COLLAPSED_WEEK_COUNT;
	// Memoized so the page keeps one identity between renders: the list filters its days by it.
	const pageStart = useMemo(() => getWeekStart(pageAnchor, weekStartDay), [pageAnchor, weekStartDay]);
	const pageEnd = useMemo(() => pageStart.plus({ weeks: weekCount, days: -1 }), [pageStart, weekCount]);
	const firstPageStart = bounds && getWeekStart(bounds.firstDay, weekStartDay);
	const canGoPrev = firstPageStart !== undefined && pageStart > firstPageStart;
	const canGoNext = bounds !== undefined && pageEnd < bounds.lastDay.startOf("day");

	const showPage = (start: DateTime): void => {
		setPageAnchor(start);
		setSelection({ date: isWithin(today, start, start.plus({ weeks: weekCount, days: -1 })) ? today : start, scroll: "instant" });
	};

	return {
		pageStart,
		pageEnd,
		weekCount,
		isExpanded,
		selection,
		canGoPrev,
		canGoNext,
		isAwayFromToday: !selection.date.hasSame(today, "day"),
		goPrev: () => {
			if (!firstPageStart || !canGoPrev) return;
			const start = pageStart.minus({ weeks: weekCount });
			showPage(start < firstPageStart ? firstPageStart : start);
		},
		goNext: () => {
			if (!canGoNext) return;
			showPage(pageStart.plus({ weeks: weekCount }));
		},
		goToday: () => {
			setPageAnchor(today);
			setSelection({ date: today, scroll: isWithin(today, pageStart, pageEnd) ? "smooth" : "instant" });
		},
		toggleExpanded: () => {
			setIsExpanded(!isExpanded);
			setPageAnchor(selection.date);
			setSelection({ date: selection.date, scroll: "instant" });
		},
		selectDate: (date) => {
			setSelection({ date: date.startOf("day"), scroll: "smooth" });
		},
		followListScroll: (date) => {
			setSelection((current) => (current.date.hasSame(date, "day") ? current : { date: date.startOf("day"), scroll: "none" }));
		},
	};
};
